import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MomentumApp } from './MomentumApp';
import { GraphApp } from './GraphApp';

type AppView = 'landing' | 'momentum' | 'graph' | 'complete';

export const ShippingSchoolApp: React.FC = () => {
  const [currentView, setCurrentView] = useState<AppView>('landing');
  const [completedProjects, setCompletedProjects] = useState<number>(0);

  const projects = [
    {
      id: 1,
      title: '🚀 Global API',
      description: 'Deploy a serverless function to 300+ cities worldwide',
      time: '20 min',
      component: null
    },
    {
      id: 2,
      title: '📊 Momentum App',
      description: 'Build the real-time dashboard tracking all builders',
      time: '40 min',
      component: MomentumApp
    },
    {
      id: 3,
      title: '🌐 Network Graph',
      description: 'Connect your node to the global builder network',
      time: '60 min',
      component: GraphApp
    }
  ];

  const handleStartProject = (projectId: number) => {
    if (projectId === 2) {
      setCurrentView('momentum');
    } else if (projectId === 3) {
      setCurrentView('graph');
    } else {
      // Simulate Project 1 completion
      setTimeout(() => {
        setCompletedProjects(1);
      }, 2000);
    }
  };

  const handleCompleteProject = (projectId: number) => {
    setCompletedProjects(projectId);
    if (projectId === 3) {
      setCurrentView('complete');
    } else {
      setCurrentView('landing');
    }
  };

  if (currentView === 'momentum') {
    return (
      <div className="relative">
        <MomentumApp />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 right-6 z-50"
        >
          <button
            onClick={() => handleCompleteProject(2)}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-bold shadow-lg hover:shadow-xl transition-all"
          >
            🎉 Project 2 Complete!
          </button>
        </motion.div>
      </div>
    );
  }

  if (currentView === 'graph') {
    return (
      <div className="relative">
        <GraphApp />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 right-6 z-50"
        >
          <button
            onClick={() => handleCompleteProject(3)}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-bold shadow-lg hover:shadow-xl transition-all"
          >
            🎓 Graduate Now!
          </button>
        </motion.div>
      </div>
    );
  }

  if (currentView === 'complete') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center text-white max-w-4xl mx-auto px-6"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: "spring" }}
            className="text-8xl mb-6"
          >
            🎓
          </motion.div>
          <h1 className="text-6xl font-bold mb-6 bg-blue-400 bg-clip-text text-transparent">
            GRADUATION COMPLETE!
          </h1>
          <p className="text-2xl mb-8 opacity-90">
            You just shipped 3 real applications and joined a global network of builders
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <h3 className="text-xl font-bold mb-2">🚀 Global API</h3>
              <p className="opacity-80">Deployed to 300+ cities</p>
              <div className="text-green-400 font-semibold">✅ LIVE</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <h3 className="text-xl font-bold mb-2">📊 Momentum App</h3>
              <p className="opacity-80">Real-time builder tracking</p>
              <div className="text-green-400 font-semibold">✅ LIVE</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <h3 className="text-xl font-bold mb-2">🌐 Network Graph</h3>
              <p className="opacity-80">Connected to the mesh</p>
              <div className="text-green-400 font-semibold">✅ CONNECTED</div>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-xl">
              <strong>"Holy shit, I just shipped 3 real applications and I actually understand CloudFlare now"</strong>
            </p>
            <p className="text-lg opacity-75">
              Share your success and help others join the network
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <button className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all">
              Share Your Portfolio
            </button>
            <button 
              onClick={() => setCurrentView('landing')}
              className="border border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-all"
            >
              View Your Journey
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-blue-600 p-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto text-center"
        >
          <h1 className="text-6xl font-bold mb-4">🎓 Shipping School</h1>
          <p className="text-2xl opacity-90 mb-6">
            Ship 3 real apps in 2 hours (or get your money back)
          </p>
          <div className="text-lg opacity-80">
            Join 1,247+ developers who went from CloudFlare confused to confidently shipping
          </div>
        </motion.div>
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Your Progress</h2>
            <div className="text-sm opacity-75">
              {completedProjects}/3 Projects Complete
            </div>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-3">
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: `${(completedProjects / 3) * 100}%` }}
              transition={{ duration: 0.5 }}
              className="bg-blue-500 h-3 rounded-full"
            />
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {projects.map((project, index) => {
            const isCompleted = completedProjects >= project.id;
            const isNext = completedProjects + 1 === project.id;
            const isLocked = completedProjects < project.id - 1;

            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-xl p-6 transition-all duration-300 ${
                  isCompleted
                    ? 'bg-gradient-to-br from-green-600/20 to-emerald-600/20 border border-green-500/30'
                    : isNext
                    ? 'bg-gradient-to-br bg-blue-600/20 border border-blue-500/30 ring-2 ring-blue-400/50'
                    : isLocked
                    ? 'bg-slate-800/30 border border-slate-600/30 opacity-50'
                    : 'bg-slate-800/50 border border-slate-600/50'
                }`}
              >
                {isCompleted && (
                  <div className="absolute top-4 right-4 text-green-400 text-2xl">
                    ✅
                  </div>
                )}
                {isNext && (
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute top-4 right-4 text-yellow-400 text-2xl"
                  >
                    ⭐
                  </motion.div>
                )}
                
                <h3 className="text-2xl font-bold mb-3">{project.title}</h3>
                <p className="text-lg opacity-80 mb-4">{project.description}</p>
                <div className="text-sm opacity-60 mb-6">⏱️ {project.time}</div>
                
                <button
                  onClick={() => handleStartProject(project.id)}
                  disabled={isLocked}
                  className={`w-full py-3 rounded-lg font-bold transition-all ${
                    isCompleted
                      ? 'bg-green-600 text-white cursor-default'
                      : isNext
                      ? 'bg-blue-600 text-white hover:shadow-lg'
                      : isLocked
                      ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {isCompleted ? 'Completed ✅' : isNext ? 'Start Building 🚀' : isLocked ? 'Locked 🔒' : 'Ready'}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Money Back Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-orange-600/20 border border-yellow-500/30 rounded-xl p-8 text-center"
        >
          <h3 className="text-3xl font-bold mb-4">💰 No-Questions-Asked Guarantee</h3>
          <p className="text-xl mb-6">
            If you don't ship 3 real applications today, we <strong>insist</strong> you take your money back.
          </p>
          <p className="text-lg opacity-80">
            But you won't want it back. 😉
          </p>
        </motion.div>

        {/* Social Proof */}
        <div className="mt-12 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-800/50 rounded-xl p-6">
              <div className="text-3xl font-bold text-blue-400 mb-2">1,247</div>
              <div className="opacity-75">Graduates</div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6">
              <div className="text-3xl font-bold text-green-400 mb-2">23</div>
              <div className="opacity-75">Building Right Now</div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6">
              <div className="text-3xl font-bold text-blue-400 mb-2">156</div>
              <div className="opacity-75">This Week</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};